import { action, observable, runInAction, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import { AxiosError } from 'axios';

import DataMixin from 'utils/stores/DataMixin';

import Logger from 'utils/logger';

import API from 'utils/api';
import { EventType } from 'utils/events/constants';
import { isNotFound } from 'utils/api/errors';

import { UuidableName } from 'utils/api/types';
import { ExtraParamsForCreatingEntity } from 'utils/api/modules/entities/entities.types';

import RootStore from '../Root';
import DetailsForm from './DetailsForm';
import InspectionActivities from './InspectionActivities';

export default class InspectionDetailsStore extends DataMixin {
  @observable error?: AxiosError;

  @observable details?: DetailsForm;

  @observable activities?: InspectionActivities;

  @observable extraParamsForCreatingInspection?: ExtraParamsForCreatingEntity;

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api);

    makeObservable(this);

    eventBus.register(this);
  }

  @action.bound
  async loadDetails(formId: string): Promise<void> {
    if (this.isLoaded && this.details?.uuid === formId) {
      return;
    }

    this.isLoaded = false;
    try {
      const { data } = await this.api.workflows.loadFormsDetails(formId)();
      runInAction(() => {
        this.error = undefined;
        this.details = new DetailsForm(this.store, this.api, formId, data);
        this.activities = new InspectionActivities(this.store, this.api, formId);
      });
    } catch (e) {
      runInAction(() => {
        // @ts-ignore
        this.error = e;
      });
      // @ts-ignore
      if (!isNotFound(e)) {
        Logger.error(`Invalid load inspection details API response. Form id: ${formId}`, e);
      }
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  @action.bound
  setExtraParamsForCreatingInspection(params?: ExtraParamsForCreatingEntity): void {
    this.extraParamsForCreatingInspection = params;
  }

  async createInspection(templateVersion: string, name: string): Promise<UuidableName | null> {
    const site = this.extraParamsForCreatingInspection ? this.extraParamsForCreatingInspection.sites : undefined;
    const asset = this.extraParamsForCreatingInspection ? this.extraParamsForCreatingInspection.assets : undefined;
    const event = this.extraParamsForCreatingInspection ? this.extraParamsForCreatingInspection.events : undefined;
    const document = this.extraParamsForCreatingInspection
      ? this.extraParamsForCreatingInspection.documents
      : undefined;
    const contact = this.extraParamsForCreatingInspection ? this.extraParamsForCreatingInspection.contacts : undefined;

    try {
      const { data } = await this.api.workflows.createInspection({
        templateVersion,
        name,
        site,
        asset,
        event,
        document,
        contact,
      })();
      this.store.notification.enqueueSuccessSnackbar('Form created successfully.');

      return data;
    } catch (e) {
      this.store.notification.enqueueErrorSnackbar('Failed to create form.');
      if (!isNotFound(e as AxiosError)) {
        Logger.error('Failed to create form.', e);
      }
      return null;
    } finally {
      this.extraParamsForCreatingInspection = undefined;
    }
  }

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.isLoaded = false;
    this.error = undefined;
    this.details = undefined;
    this.activities = undefined;
  }
}
